import { Command } from '../commands/Command';

/**
 * 차트 안내 박스(HTML 기반) 정의
 */
export interface NextBtnBox {
  id: string;
  title: string;
  content: string | string[];
  x: number | string;
  y: number | string;
  width?: number | string;
  nextStep: number;
  btnContent?: string;
  BoxStyle?: { [key: string]: any };
  titleStyle?: { [key: string]: any };
  contentStyle?: { [key: string]: any };
  btnStyle?: { [key: string]: any };
  style?: { [key: string]: any }; 
}

/**
 * 마스크(구멍 뚫기) 데이터 정의
 */
export interface MaskConfig {
  id: string;
  type: 'circle' | 'rect' | 'triangle';
  x: number | string;
  y: number | string;
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  borderRadius?: number;
  points?: string;
}

/**
 * 도형(Object) 데이터 정의
 */
export interface ChartObject {
  id: string;
  type: 'rect' | 'circle' | 'triangle' | 'mask';
  x: number | string;
  y: number | string;
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  points?: string;
  style: {
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    opacity?: number;
    [key: string]: any;
  };
}

/**
 * 그래프 엘리먼트 정의 (데이터 기반)
 */
export interface GraphElement {
  id: string;
  type: 'line' | 'candle' | 'bar' | 'point';
  usedFields: string[];
  mapping: { [key: string]: string };
  style: any;
  transform?: {
    expression?: string;
    window?: { func: 'avg' | 'sum' | 'max'; period: number; field: string; };
  };
}

/**
 * 레이어 정의
 */
export interface Layer {
  id: string;
  elements: GraphElement[];
  objects: ChartObject[];
  masks: MaskConfig[]; 
  htmlBoxes: NextBtnBox[];
  width?: string | number;
  height?: string | number;
  top?: string | number;
  left?: string | number;
  zIndex?: number;
  style?: { backgroundColor?: string; opacity?: number; [key: string]: any; };
  axes?: {
    yAxis?: {
      orient: 'left' | 'right';
      ticks?: number;
      showGrid?: boolean;
      yMinAxisSource?: number | string[] | 'all';
      yMaxAxisSource?: number | string[] | 'all';
    };
    xAxis?: { orient: 'bottom'; showGrid?: boolean; };
  };
}

/**
 * 영역 정의
 */
export interface Region {
  id: string;
  name: string;
  width: string | number;
  height: string | number;
  top: string | number;
  left: string | number;
  layers: Layer[];
  style?: { backgroundColor?: string; border?: string; borderRadius?: any; zIndex?: number; [key: string]: any; };
}

/**
 * 차트 엔진 코어
 */
export class ChartEngine {
  private data: any[] = [];
  private regions: Region[] = [];
  private commandHistory: Command[] = [];
  private listeners: (() => void)[] = [];

  // --- 데이터 관리 ---
  addData(newData: any[]) {
    // [수정] 빈 배열이 들어오면 데이터를 초기화하고, 값이 있으면 누적합니다.
    if (!newData || newData.length === 0) {
      this.data = [];
    } else {
      this.data = [...this.data, ...newData];
    }
    this.notifyListeners();
  }

  getData() { return this.data; }

  setData(newData: any[]) {
    this.data = newData;
    this.notifyListeners();
  }

  // 모든 리전과 데이터를 한꺼번에 삭제하여 완전히 초기화
  resetAll() {
    this.data = [];
    this.regions = [];
    this.commandHistory = [];
    // [추가] 리스너들에게 상태가 완전히 비워졌음을 알림
    this.notifyListeners();
  }

  // --- 커맨드 패턴 실행부 ---
  runCommand(command: Command) {
    command.execute(this);
    this.commandHistory.push(command);
    this.notifyListeners();
  }

  undo() {
    const lastCommand = this.commandHistory.pop();
    if (lastCommand) {
      lastCommand.undo(this);
      this.notifyListeners();
    }
  }

  getSnapshot(): Region[] {
    // 깊은 복사를 통해 외부에서의 의도치 않은 상태 수정을 방지
    return JSON.parse(JSON.stringify(this.regions));
  }

  // --- Region 관리 ---
  addRegion(regionData: Omit<Region, 'layers'>) {
    if (this.regions.some(r => r.id === regionData.id)) return;
    this.regions.push({ ...regionData, layers: [] });
  }

  removeRegion(regionId: string) {
    this.regions = this.regions.filter(r => r.id !== regionId);
  }

  // --- Layer 관리 ---
  addLayerToRegion(regionId: string, layerData: Omit<Layer, 'elements' | 'objects' | 'htmlBoxes' | 'masks'>) {
    const region = this.regions.find(r => r.id === regionId);
    if (region && !region.layers.some(l => l.id === layerData.id)) {
      region.layers.push({ 
        ...layerData, 
        elements: [], 
        objects: [], 
        masks: [], 
        htmlBoxes: [] 
      } as Layer);
    }
  }

  removeLayerFromRegion(regionId: string, layerId: string) {
    const region = this.regions.find(r => r.id === regionId);
    if (region) {
      region.layers = region.layers.filter(l => l.id !== layerId);
    }
  }

  // --- Graph Element 관리 ---
  addElementToLayer(regionId: string, layerId: string, element: GraphElement) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer) {
      layer.elements.push(element);
    }
  }

  removeElementFromLayer(regionId: string, layerId: string, elementId: string) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer) {
      layer.elements = layer.elements.filter(el => el.id !== elementId);
    }
  }

  // --- Chart Object 관리 ---
  addObjectToLayer(regionId: string, layerId: string, object: ChartObject) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer) {
      if (!layer.objects) layer.objects = [];
      layer.objects.push(object);
    }
  }

  removeObjectFromLayer(regionId: string, layerId: string, objectId: string) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer && layer.objects) {
      layer.objects = layer.objects.filter(obj => obj.id !== objectId);
    }
  }

  // --- Mask 관리 ---
  addMaskToLayer(regionId: string, layerId: string, mask: MaskConfig) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer) {
      if (!layer.masks) layer.masks = [];
      const index = layer.masks.findIndex(m => m.id === mask.id);
      if (index > -1) {
        layer.masks[index] = mask; 
      } else {
        layer.masks.push(mask);    
      }
    }
  }

  removeMaskFromLayer(regionId: string, layerId: string, maskId: string) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer && layer.masks) {
      layer.masks = layer.masks.filter(m => m.id !== maskId);
    }
  }

  // --- NextBtnBox 관리 ---
  addNextBtnBoxToLayer(regionId: string, layerId: string, box: NextBtnBox) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer) {
      if (!layer.htmlBoxes) layer.htmlBoxes = [];
      layer.htmlBoxes.push(box);
    }
  }

  removeNextBtnBoxFromLayer(regionId: string, layerId: string, boxId: string) {
    const region = this.regions.find(r => r.id === regionId);
    const layer = region?.layers.find(l => l.id === layerId);
    if (layer && layer.htmlBoxes) {
      layer.htmlBoxes = layer.htmlBoxes.filter(box => box.id !== boxId);
    }
  }

  // --- 이벤트 구독 시스템 ---
  private notifyListeners() { 
    this.listeners.forEach(l => l()); 
  }

  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => { 
      this.listeners = this.listeners.filter(l => l !== listener); 
    };
  }
}